import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Hls from 'hls.js';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvHlsPlayerProps {
  src: string;
  poster: string | null;
  title: string;
  onError: () => void;
}

const HLS_MIME_TYPE = 'application/vnd.apple.mpegurl';

const OvHlsPlayer: FC<OvHlsPlayerProps> = ({ src, poster, title, onError }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const reportError = useRef(onError);
  reportError.current = onError;

  useEffect(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (video.canPlayType(HLS_MIME_TYPE)) {
      video.src = src;

      return () => video.removeAttribute('src');
    }

    if (!Hls.isSupported()) {
      reportError.current();

      return;
    }

    const hls = new Hls();
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data.fatal) {
        reportError.current();
      }
    });
    hls.loadSource(src);
    hls.attachMedia(video);

    return () => hls.destroy();
  }, [src]);

  return (
    <Video
      ref={videoRef}
      controls
      playsInline
      poster={poster ?? undefined}
      aria-label={t('videos.player.label', { title })}
      onError={() => reportError.current()}
    />
  );
};

export default OvHlsPlayer;

const Video = styled.video`
  display: block;
  width: 100%;
  max-height: ${Variables.layout.PREVIEW_MAX_HEIGHT};
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.OV_BASE};
`;
